/**
 * Hook React Query para enviar las plantillas transaccionales de correo
 * de seguimiento (CallSidebar post_call).
 *
 * Las plantillas son prehechas — el agente sólo elige cuál mandar. No hay
 * personalización del cuerpo: el backend interpola nombre del lead +
 * promo del mes desde `SystemSettings.follow_up_promo_text` y la envía
 * por Mailgun/SES.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { followUpEmailService } from '@/services/followUpEmailService';
import { getErrorMessage } from '@/utils/errorHandler';
import type {
  FollowUpEmailTemplateId,
  SendFollowUpEmailResponse,
} from '@/types/followUpEmail';

interface SendMutationArgs {
  template_id: FollowUpEmailTemplateId;
}

export function useSendFollowUpEmail(contactId: string) {
  const queryClient = useQueryClient();

  return useMutation<SendFollowUpEmailResponse, unknown, SendMutationArgs>({
    mutationFn: ({ template_id }) =>
      followUpEmailService.send({ contact_id: contactId, template_id }),
    onSuccess: (response) => {
      toast.success(
        response.to_email
          ? `Correo enviado a ${response.to_email}`
          : 'Correo enviado',
      );
      // Refrescar timeline del contacto: el backend emite una CRMActivity
      // por cada envío que debe aparecer en el historial.
      queryClient.invalidateQueries({
        queryKey: ['contactCommunications', contactId],
      });
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
      queryClient.invalidateQueries({
        queryKey: ['contactActivity', contactId],
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
